import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { type R2Progression, readProgressApi } from "@/api/readProgress";
import { isOfflineQueuedError } from "@/lib/offline/outbox";
import { useReadingSession } from "./useReadingSession";

const STORAGE_KEY_PREFIX = "epub-cfi-";
const STORAGE_TIMESTAMP_PREFIX = "epub-cfi-ts-";

// Threshold for considering percentage as "changed" (avoids saving tiny changes)

const CODEX_DEVICE_ID = "codex-web";
const CODEX_DEVICE_NAME = "Codex Web Reader";

interface UseEpubProgressOptions {
  /** Book ID for storing progress */
  bookId: string;
  /** Total pages in the book (used as fallback page number) */
  totalPages: number;
  /** Debounce delay for saving progress (ms) */
  debounceMs?: number;
  /** Whether to enable progress tracking */
  enabled?: boolean;
}

interface UseEpubProgressReturn {
  /** Get the saved CFI location for this book (null if none saved) */
  getSavedLocation: () => string | null;
  /** Get the localStorage timestamp for this book (null if none saved) */
  getLocalTimestamp: () => string | null;
  /** Get the initial percentage from API (for cross-device sync) */
  initialPercentage: number | null;
  /** Get the initial CFI from R2Progression (for cross-device sync with Codex web) */
  initialCfi: string | null;
  /** Get the href from R2Progression locator (for cross-app spine navigation) */
  initialHref: string | null;
  /** Get the within-resource progression from R2Progression (0.0-1.0 within the href) */
  initialProgression: number | null;
  /** Get the R2Progression modified timestamp (for cross-device sync) */
  apiTimestamp: string | null;
  /** Get the device ID that last saved the R2Progression */
  apiDeviceId: string | null;
  /** Whether API progress is still loading */
  isLoadingProgress: boolean;
  /** Save the current CFI location, percentage, and chapter href */
  saveLocation: (cfi: string, percentage: number, href: string) => void;
  /** Clear saved progress for this book */
  clearProgress: () => void;
}

/**
 * Hook for managing EPUB reading progress via CFI (Canonical Fragment Identifier).
 *
 * Stores CFI locations in localStorage for precise position restoration.
 * Syncs R2Progression (Readium standard) to the backend API for cross-device
 * and cross-app sync (e.g., between Codex web reader and Komic).
 * Also syncs percentage-based progress to the backend API for backwards compat.
 * Uses debouncing to avoid excessive writes during rapid navigation.
 */
export function useEpubProgress({
  bookId,
  totalPages,
  debounceMs = 1000,
  enabled = true,
}: UseEpubProgressOptions): UseEpubProgressReturn {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedCfiRef = useRef<string | null>(null);
  const pendingCfiRef = useRef<string | null>(null);
  const pendingPercentageRef = useRef<number>(0);
  const pendingHrefRef = useRef<string>("");
  // Latch so passing the completion threshold emits one completed session
  // rather than one per location change from there to the end of the book.
  const completionReportedRef = useRef(false);

  // Reading time and progress measured alongside position. Disabled with
  // progress tracking, so incognito reading records neither.
  const { recordActivity, markCompleted } = useReadingSession({
    bookId,
    enabled,
  });

  // Fetch initial progress from API for cross-device sync
  const { data: apiProgress, isLoading: isLoadingProgress } = useQuery({
    queryKey: ["readProgress", bookId],
    queryFn: () => readProgressApi.get(bookId),
    enabled: enabled && !!bookId,
    staleTime: 30000,
  });

  // Fetch R2Progression for cross-device/cross-app sync
  const { data: r2Progression, isLoading: isLoadingProgression } = useQuery({
    queryKey: ["progression", bookId],
    queryFn: () => readProgressApi.getProgression(bookId),
    enabled: enabled && !!bookId,
    staleTime: 30000,
  });

  // Get initial percentage from API or R2Progression
  const progressWithPercentage = apiProgress as
    | (typeof apiProgress & { progress_percentage?: number | null })
    | null
    | undefined;

  // Prefer R2Progression totalProgression, fall back to legacy progress_percentage
  const initialPercentage =
    r2Progression?.locator?.locations?.totalProgression ??
    progressWithPercentage?.progress_percentage ??
    null;

  // Get CFI from R2Progression if it was saved by Codex web (has cfi extension)
  const initialCfi = r2Progression?.locator?.locations?.cfi ?? null;

  // Get href and within-resource progression for cross-app spine-based navigation
  const initialHref = r2Progression?.locator?.href ?? null;
  const initialProgression =
    r2Progression?.locator?.locations?.progression ?? null;

  // Get the R2Progression modified timestamp for cross-device comparison
  const apiTimestamp = r2Progression?.modified ?? null;

  // Get the device ID that last saved the R2Progression
  const apiDeviceId = r2Progression?.device?.id ?? null;

  // Store refs to avoid dependency issues
  const bookIdRef = useRef(bookId);
  const totalPagesRef = useRef(totalPages);

  useEffect(() => {
    bookIdRef.current = bookId;
    totalPagesRef.current = totalPages;
  }, [bookId, totalPages]);

  const storageKey = `${STORAGE_KEY_PREFIX}${bookId}`;
  const timestampKey = `${STORAGE_TIMESTAMP_PREFIX}${bookId}`;

  // Get saved location from localStorage
  const getSavedLocation = useCallback((): string | null => {
    if (!enabled) return null;
    try {
      return localStorage.getItem(storageKey);
    } catch {
      console.warn("Failed to read EPUB progress from localStorage");
      return null;
    }
  }, [storageKey, enabled]);

  // Get saved timestamp from localStorage
  const getLocalTimestamp = useCallback((): string | null => {
    if (!enabled) return null;
    try {
      return localStorage.getItem(timestampKey);
    } catch {
      return null;
    }
  }, [timestampKey, enabled]);

  // Save location to localStorage (internal, immediate)
  const saveToStorage = useCallback(
    (cfi: string) => {
      if (!enabled) return;
      try {
        localStorage.setItem(storageKey, cfi);
        localStorage.setItem(timestampKey, new Date().toISOString());
        lastSavedCfiRef.current = cfi;
      } catch {
        console.warn("Failed to save EPUB progress to localStorage");
      }
    },
    [storageKey, timestampKey, enabled],
  );

  // Save progress to backend API (both legacy progress and R2Progression)
  const saveToBackend = useCallback(
    (percentage: number, cfi: string, href: string) => {
      const currentBookId = bookIdRef.current;
      const currentTotalPages = totalPagesRef.current;

      const currentPage =
        currentTotalPages > 0
          ? Math.max(1, Math.round(percentage * currentTotalPages))
          : Math.max(1, Math.round(percentage * 100));

      const isCompleted = percentage >= 0.98;

      // Build R2Progression
      const progression: R2Progression = {
        device: { id: CODEX_DEVICE_ID, name: CODEX_DEVICE_NAME },
        locator: {
          href,
          locations: {
            position: currentPage,
            totalProgression: percentage,
            cfi,
          },
          type: "application/xhtml+xml",
        },
        modified: new Date().toISOString(),
      };

      // Save both in parallel
      Promise.all([
        readProgressApi.update(currentBookId, {
          currentPage,
          progressPercentage: percentage,
          completed: isCompleted,
        }),
        readProgressApi.updateProgression(currentBookId, progression),
      ])
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["readProgress", currentBookId],
          });
          queryClient.invalidateQueries({
            queryKey: ["progression", currentBookId],
          });
          queryClient.invalidateQueries({
            queryKey: ["book", currentBookId],
          });
          queryClient.invalidateQueries({
            queryKey: ["books", "in-progress"],
          });
          queryClient.invalidateQueries({
            queryKey: ["books", "recently-read"],
          });
        })
        .catch((error) => {
          // Both calls run via Promise.all; if either was queued for
          // offline delivery, the rejection lands here. Skip the console
          // error in that case so the offline path stays quiet.
          if (isOfflineQueuedError(error)) return;
          console.error("Failed to save EPUB reading progress:", error);
        });
    },
    [queryClient],
  );

  // Debounced save - public API
  const saveLocation = useCallback(
    (cfi: string, percentage: number, href: string) => {
      if (!enabled) return;

      // A location change is the activity signal for reflowable content:
      // there are no page turns to observe, only movement through the book.
      recordActivity({ percentage });
      if (percentage >= 0.98 && !completionReportedRef.current) {
        // Matches the completion threshold saveToBackend applies. Latched so
        // reading on past 98% does not emit a completed session per location.
        completionReportedRef.current = true;
        markCompleted({ percentage });
      }

      // Store pending values for flush on unmount
      pendingCfiRef.current = cfi;
      pendingPercentageRef.current = percentage;
      pendingHrefRef.current = href;

      // Skip if nothing changed
      const cfiChanged = cfi !== lastSavedCfiRef.current;
      if (!cfiChanged) {
        return;
      }

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const cfiToSave = cfi;
      const percentageToSave = percentage;
      const hrefToSave = href;

      debounceTimerRef.current = setTimeout(() => {
        saveToStorage(cfiToSave);
        saveToBackend(percentageToSave, cfiToSave, hrefToSave);
        pendingCfiRef.current = null;
        pendingPercentageRef.current = 0;
        pendingHrefRef.current = "";
      }, debounceMs);
    },
    [
      enabled,
      debounceMs,
      saveToStorage,
      saveToBackend,
      recordActivity,
      markCompleted,
    ],
  );

  // Clear progress
  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      lastSavedCfiRef.current = null;
      pendingCfiRef.current = null;
    } catch {
      console.warn("Failed to clear EPUB progress from localStorage");
    }
  }, [storageKey]);

  // Store enabled ref for cleanup
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Cleanup: save pending progress on unmount
  useEffect(() => {
    const currentStorageKey = storageKey;

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!enabledRef.current) {
        return;
      }

      // Flush any pending CFI to localStorage
      if (
        pendingCfiRef.current &&
        pendingCfiRef.current !== lastSavedCfiRef.current
      ) {
        try {
          localStorage.setItem(currentStorageKey, pendingCfiRef.current);
          localStorage.setItem(
            `${STORAGE_TIMESTAMP_PREFIX}${bookIdRef.current}`,
            new Date().toISOString(),
          );
        } catch {
          // Ignore errors on unmount
        }
      }

      // Flush any pending percentage to backend
      if (pendingPercentageRef.current >= 0 && pendingCfiRef.current) {
        const currentBookId = bookIdRef.current;
        const currentTotalPages = totalPagesRef.current;
        const percentage = pendingPercentageRef.current;
        const cfi = pendingCfiRef.current;
        const href = pendingHrefRef.current;
        const currentPage =
          currentTotalPages > 0
            ? Math.max(1, Math.round(percentage * currentTotalPages))
            : Math.max(1, Math.round(percentage * 100));

        const isCompleted = percentage >= 0.98;

        // Save both legacy progress and R2Progression on unmount
        readProgressApi
          .update(currentBookId, {
            currentPage,
            progressPercentage: percentage,
            completed: isCompleted,
          })
          .catch(() => {});

        readProgressApi
          .updateProgression(currentBookId, {
            device: { id: CODEX_DEVICE_ID, name: CODEX_DEVICE_NAME },
            locator: {
              href,
              locations: {
                position: currentPage,
                totalProgression: percentage,
                cfi,
              },
              type: "application/xhtml+xml",
            },
            modified: new Date().toISOString(),
          })
          .catch(() => {});
      }
    };
  }, [storageKey]);

  return {
    getSavedLocation,
    getLocalTimestamp,
    initialPercentage,
    initialCfi,
    initialHref,
    initialProgression,
    apiTimestamp,
    apiDeviceId,
    isLoadingProgress: isLoadingProgress || isLoadingProgression,
    saveLocation,
    clearProgress,
  };
}
